/**
 * Individual Calculator Component
 * Calculate SSP for a single employee absence with 8-week AWE
 */

import React, { useMemo, useState } from 'react';
import {
  Calculator,
  RefreshCw,
  Download,
  ArrowRight,
  Check,
  X,
  Info,
  Sparkles,
  AlertCircle,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { useIndividualCalculator } from '../hooks/useSSPState';
import { 
  calculateAWE, 
  calculateSSPCurrent, 
  calculateSSP2026,
  calculateBackfillCost,
  formatCurrency,
} from '../utils/sspCalculations';
import { exportIndividualToPDF } from '../utils/exportUtils';
import { LOWER_EARNINGS_LIMIT, SSP_WEEKLY_RATE } from '../data/constants';
import Tooltip from './Tooltip';
import { TOOLTIP_CONTENT } from '../data/tooltipContent.jsx';
import CalculationBreakdown from './CalculationBreakdown';

export default function IndividualCalculator() {
  const [showInputs, setShowInputs] = useState(true);

  const {
    employee,
    earnings,
    absence,
    updateEmployee,
    updateEarning,
    updateAbsence,
    calculateFromPattern,
    reset,
  } = useIndividualCalculator();

  // Bidirectional: sync rostered days with probability
  const handleRosteredDaysChange = (value) => {
    const days = parseInt(value) || 0;
    updateAbsence({ rosteredDays: days });
  };
  
  const handleProbabilityChange = (probability) => {
    const rosteredDays = Math.round(absence.totalDays * probability);
    updateAbsence({ rosteredDays });
  };
  
  // Calculate probability from current values
  const rotaProbability = absence.totalDays > 0 
    ? absence.rosteredDays / absence.totalDays 
    : 0;
  
  // Calculate results
  const results = useMemo(() => {
    const aweResult = calculateAWE(earnings, {
      payFrequency: employee.payFrequency,
      weeksEmployed: employee.weeksEmployed,
    });

    const currentSSP = calculateSSPCurrent({
      awe: aweResult.awe,
      qualifyingDays: employee.qualifyingDays,
      totalSickDays: absence.totalDays,
      rosteredDays: absence.rosteredDays,
      isLinkedPeriod: absence.isLinkedPeriod,
      previousSSPWeeks: absence.previousSSPWeeks,
    });

    const newSSP = calculateSSP2026({
      awe: aweResult.awe,
      qualifyingDays: employee.qualifyingDays,
      totalSickDays: absence.totalDays,
      rosteredDays: absence.rosteredDays,
      previousSSPWeeks: absence.previousSSPWeeks,
    });

    const backfill = calculateBackfillCost({
      rosteredDays: absence.rosteredDays,
      shiftLength: employee.typicalShiftLength,
      hourlyRate: employee.hourlyRate,
    });

    return { aweResult, currentSSP, newSSP, backfill };
  }, [employee, earnings, absence]);
  
  const handleExport = () => {
    exportIndividualToPDF({
      employee,
      aweResult: results.aweResult,
      currentSSP: results.currentSSP,
      newSSP: results.newSSP,
      backfill: results.backfill,
    });
  };
  
  return (
    <div className="space-y-6">
      {/* Intro */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">Individual SSP Calculator</p>
            <p>
              Calculate SSP for a specific employee absence. Enter their 8-week earnings history 
              to determine AWE and eligibility, then input the absence details.
            </p>
          </div>
        </div>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Inputs */}
        <div className="lg:col-span-2">
          {/* Collapsible Header */}
          <button
            onClick={() => setShowInputs(!showInputs)}
            className="w-full flex items-center justify-between p-4 bg-slate-50 hover:bg-slate-100 rounded-lg border border-slate-200 transition-colors mb-4"
          >
            <div className="flex items-center gap-2">
              <Calculator className="w-5 h-5 text-slate-600" />
              <span className="font-semibold text-slate-900">Input Details</span>
              <span className="text-xs text-slate-500">(Employee, Earnings & Absence)</span>
            </div>
            {showInputs ? <ChevronUp className="w-5 h-5 text-slate-600" /> : <ChevronDown className="w-5 h-5 text-slate-600" />}
          </button>

          {showInputs && (
            <div className="space-y-6 animate-fade-in">
              {/* Employee Setup */}
              <div className="card">
                <div className="card-header flex justify-between items-center">
                  <h2 className="font-semibold text-slate-900">Employee Details</h2>
                  <div className="flex items-center gap-2">
                    <button onClick={reset} className="btn-ghost btn-xs">
                      <RefreshCw className="w-4 h-4" />
                      Reset
                    </button>
                  </div>
                </div>

                <div className="card-body">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Employee ID</label>
                    <input
                      type="text"
                      value={employee.id}
                      onChange={(e) => updateEmployee({ id: e.target.value })}
                      placeholder="Optional"
                      className="input-field"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Contracted Hours/Wk</label>
                    <input
                      type="number"
                      value={employee.contractedHours}
                      onChange={(e) => updateEmployee({ contractedHours: parseFloat(e.target.value) || 0 })}
                      className="input-field"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Avg Pickup Hours</label>
                    <input
                      type="number"
                      value={employee.avgPickupHours}
                      onChange={(e) => updateEmployee({ avgPickupHours: parseFloat(e.target.value) || 0 })}
                      className="input-field"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Hourly Rate (£)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={employee.hourlyRate}
                      onChange={(e) => updateEmployee({ hourlyRate: parseFloat(e.target.value) || 0 })}
                      className="input-field"
                    />
                  </div>
                  <div>
                    <label className="flex items-center gap-1.5 text-sm font-medium text-slate-700 mb-1">
                      Qualifying Days/Wk
                      <Tooltip content={TOOLTIP_CONTENT.qualifyingDays} />
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="7"
                      value={employee.qualifyingDays}
                      onChange={(e) => updateEmployee({ qualifyingDays: parseInt(e.target.value) || 1 })}
                      className="input-field"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Avg Shift Length (hrs)</label>
                    <input
                      type="number"
                      step="0.5"
                      value={employee.typicalShiftLength}
                      onChange={(e) => updateEmployee({ typicalShiftLength: parseFloat(e.target.value) || 0 })}
                      className="input-field"
                    />
                  </div>

                  {/* Pay Frequency */}
                  <div className="col-span-2 md:col-span-1">
                    <label className="block text-sm font-medium text-slate-700 mb-2">Pay Frequency</label>
                    <div className="flex gap-4">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="payFrequency"
                          value="weekly"
                          checked={employee.payFrequency === 'weekly'}
                          onChange={(e) => updateEmployee({ payFrequency: e.target.value })}
                          className="w-4 h-4 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="text-sm text-slate-700">Weekly</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="payFrequency"
                          value="monthly"
                          checked={employee.payFrequency === 'monthly'}
                          onChange={(e) => updateEmployee({ payFrequency: e.target.value })}
                          className="w-4 h-4 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="text-sm text-slate-700">Monthly</span>
                      </label>
                    </div>
                  </div>

                  {/* Employment Duration */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Weeks Employed
                      <span className="text-xs text-slate-500 ml-1">(for new employees)</span>
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="52"
                      value={employee.weeksEmployed}
                      onChange={(e) => updateEmployee({ weeksEmployed: parseInt(e.target.value) || 1 })}
                      className="input-field"
                    />
                  </div>
                  </div>
                </div>
              </div>

              {/* 8-Week Earnings */}
              <div className="card">
                <div className="card-header flex justify-between items-center">
                  <div>
                    <h2 className="flex items-center gap-2 font-semibold text-slate-900">
                      8-Week Earnings History
                      <Tooltip content={TOOLTIP_CONTENT.eightWeekEarnings} />
                    </h2>
                    <p className="text-xs text-slate-500 mt-0.5">Enter gross earnings for each of the last 8 weeks</p>
                  </div>
                  <button 
                onClick={calculateFromPattern}
                className="btn-secondary btn-sm"
                title="Generate sample earnings based on employee pattern"
              >
                <Sparkles className="w-4 h-4" />
                Generate Sample
              </button>
            </div>
            
            <div className="card-body">
              <div className="grid grid-cols-4 md:grid-cols-8 gap-2">
                {earnings.map((earning, index) => (
                  <div key={index}>
                    <label className="block text-xs text-slate-500 mb-1 text-center">
                      Wk {index + 1}
                    </label>
                    <div className="relative">
                      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 text-sm">£</span>
                      <input
                        type="number"
                        value={earning || ''}
                        onChange={(e) => updateEarning(index, e.target.value)}
                        placeholder="0"
                        className="input-field-compact w-full pl-5 text-center"
                      />
                    </div>
                  </div>
                ))}
              </div>
              
              {/* AWE Result */}
              <div className="mt-4 p-4 bg-slate-50 rounded-lg">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div>
                    <p className="text-sm text-slate-600">Average Weekly Earnings (AWE)</p>
                    <p className="text-2xl font-bold text-slate-900 font-mono">
                      {formatCurrency(results.aweResult.awe)}
                    </p>
                    {results.aweResult.method && (
                      <p className="text-xs text-slate-500 mt-1">
                        {results.aweResult.method}
                      </p>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-slate-600">Lower Earnings Limit (LEL): £{LOWER_EARNINGS_LIMIT}/wk</p>
                    <div className={`inline-flex items-center gap-1.5 mt-1 px-3 py-1 rounded-full ${
                      results.aweResult.meetsLEL
                        ? 'bg-green-100 text-green-800'
                        : 'bg-amber-100 text-amber-800'
                    }`}>
                      {results.aweResult.meetsLEL ? (
                        <><Check className="w-4 h-4" /> Eligible for SSP (current rules)</>
                      ) : (
                        <><X className="w-4 h-4" /> Below LEL - Not eligible (current rules)</>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Absence Details */}
          <div className="card">
            <div className="card-header">
              <h2 className="font-semibold text-slate-900">Absence Details</h2>
            </div>
            
            <div className="card-body">
              <div className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <label className="flex items-center gap-1.5 text-sm font-medium text-slate-700 mb-1">
                      Total Sick Days
                      <Tooltip content={TOOLTIP_CONTENT.totalSickDays} />
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={absence.totalDays}
                      onChange={(e) => updateAbsence({ totalDays: parseInt(e.target.value) || 0 })}
                      className="input-field"
                    />
                    <p className="text-xs text-slate-500 mt-1">Calendar days off sick</p>
                  </div>

                  <div>
                    <label className="flex items-center gap-1.5 text-sm font-medium text-slate-700 mb-1">
                      Rostered Days
                      <Tooltip content={TOOLTIP_CONTENT.rosteredDays} />
                    </label>
                    <input
                      type="number"
                      min="0"
                      max={absence.totalDays}
                      value={absence.rosteredDays}
                      onChange={(e) => handleRosteredDaysChange(e.target.value)}
                      className="input-field"
                    />
                    <p className="text-xs text-slate-500 mt-1">Shifts they would have worked</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Rota Probability
                      <span className="font-normal text-slate-400 ml-1">({(rotaProbability * 100).toFixed(0)}%)</span>
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.01"
                      value={rotaProbability}
                      onChange={(e) => handleProbabilityChange(parseFloat(e.target.value))}
                      className="w-full mt-2 accent-primary-600"
                    />
                    <p className="text-xs text-slate-500 mt-1">Updates rostered days</p>
                  </div>

                  <div>
                    <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-1">
                      Linked Period?
                      <Tooltip content={TOOLTIP_CONTENT.linkedPeriod} />
                    </label>
                    <label className="flex items-center gap-2 mt-2">
                      <input
                        type="checkbox"
                        checked={absence.isLinkedPeriod}
                        onChange={(e) => updateAbsence({ isLinkedPeriod: e.target.checked })}
                        className="rounded text-primary-600 focus:ring-primary-500"
                      />
                      <span className="text-sm text-slate-600">Yes, linked to previous PIW</span>
                    </label>
                  </div>
                </div>

                {/* Previous SSP Weeks - 28-week maximum tracking */}
                <div className="border-t border-slate-200 pt-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Previous SSP Weeks Used
                        <span className="text-xs text-slate-500 ml-1">(for 28-week maximum)</span>
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="28"
                        step="0.1"
                        value={absence.previousSSPWeeks}
                        onChange={(e) => updateAbsence({ previousSSPWeeks: parseFloat(e.target.value) || 0 })}
                        className="input-field"
                      />
                      <p className="text-xs text-slate-500 mt-1">Weeks already used in this PIW or tax year</p>
                    </div>
                    {(absence.previousSSPWeeks || 0) > 0 && (
                      <div className={`p-3 rounded-lg ${
                        (absence.previousSSPWeeks || 0) >= 28
                          ? 'bg-red-50 border border-red-200'
                          : (absence.previousSSPWeeks || 0) >= 24
                          ? 'bg-amber-50 border border-amber-200'
                          : 'bg-blue-50 border border-blue-200'
                      }`}>
                        <div className="flex items-start gap-2">
                          <AlertCircle className={`w-4 h-4 mt-0.5 ${
                            (absence.previousSSPWeeks || 0) >= 28
                              ? 'text-red-600'
                              : (absence.previousSSPWeeks || 0) >= 24
                              ? 'text-amber-600'
                              : 'text-blue-600'
                          }`} />
                          <div>
                            <p className={`text-sm font-medium ${
                              (absence.previousSSPWeeks || 0) >= 28
                                ? 'text-red-800'
                                : (absence.previousSSPWeeks || 0) >= 24
                                ? 'text-amber-800'
                                : 'text-blue-800'
                            }`}>
                              {(absence.previousSSPWeeks || 0) >= 28
                                ? 'SSP Exhausted'
                                : (absence.previousSSPWeeks || 0) >= 24
                                ? 'Approaching SSP Limit'
                                : 'SSP Partially Used'
                              }
                            </p>
                            <p className={`text-xs mt-1 ${
                              (absence.previousSSPWeeks || 0) >= 28
                                ? 'text-red-700'
                                : (absence.previousSSPWeeks || 0) >= 24
                                ? 'text-amber-700'
                                : 'text-blue-700'
                            }`}>
                              {(28 - (absence.previousSSPWeeks || 0)).toFixed(1)} weeks remaining
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
            </div>
          )}

          {/* Calculation Breakdown */}
          <CalculationBreakdown
            aweResult={results.aweResult}
            currentSSP={results.currentSSP}
            newSSP={results.newSSP}
            employee={employee}
            absence={absence}
          />
        </div>

        {/* Right: Results */}
        <div className="space-y-6">
          {/* SSP Comparison */}
          <div className="card">
            <div className="card-header flex justify-between items-center">
              <h2 className="font-semibold text-slate-900">SSP Calculation</h2>
              <button onClick={handleExport} className="btn-ghost btn-xs">
                <Download className="w-4 h-4" />
                PDF
              </button>
            </div>
            
            <div className="card-body space-y-4">
              {/* Current Rules */}
              <div className="p-4 bg-primary-50 rounded-lg border border-primary-200">
                <h3 className="text-sm font-semibold text-primary-900 mb-3">Current Rules</h3>
                
                {!results.currentSSP.eligible ? (
                  <div className="text-sm text-primary-700">
                    <p className="flex items-center gap-2">
                      <X className="w-4 h-4 text-red-500" />
                      <span>{results.currentSSP.reason}</span>
                    </p>
                    <p className="mt-2 font-semibold">SSP: £0</p>
                  </div>
                ) : results.currentSSP.sspDaysPaid === 0 ? (
                  <div className="text-sm text-primary-700">
                    <p className="flex items-center gap-2">
                      <AlertCircle className="w-4 h-4 text-amber-500" />
                      <span>{results.currentSSP.reason || 'Waiting days only - no SSP payable'}</span>
                    </p>
                    <p className="mt-2 font-semibold">SSP: £0</p>
                  </div>
                ) : (
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between text-primary-700">
                      <span>Daily rate ({employee.qualifyingDays} QDs)</span>
                      <span className="font-mono">{formatCurrency(results.currentSSP.dailyRate)}</span>
                    </div>
                    <div className="flex justify-between text-primary-700">
                      <span>Waiting days</span>
                      <span className="font-mono">{results.currentSSP.waitingDays}</span>
                    </div>
                    <div className="flex justify-between text-primary-700">
                      <span>SSP days paid</span>
                      <span className="font-mono">{results.currentSSP.sspDaysPaid}</span>
                    </div>
                    <div className="flex justify-between pt-2 border-t border-primary-200 font-semibold text-primary-900">
                      <span>Total SSP</span>
                      <span className="font-mono text-lg">{formatCurrency(results.currentSSP.totalSSP)}</span>
                    </div>
                  </div>
                )}
              </div>
              
              {/* April 2026 Rules */}
              <div className="p-4 bg-accent-50 rounded-lg border border-accent-200">
                <h3 className="text-sm font-semibold text-accent-900 mb-3">April 2026 Rules</h3>
                
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between text-accent-700">
                    <span>Weekly rate</span>
                    <span className="font-mono">
                      {formatCurrency(results.newSSP.weeklyRate)}
                      {results.newSSP.isReducedRate && (
                        <span className="text-xs ml-1">(80% of AWE)</span>
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between text-accent-700">
                    <span>Daily rate ({employee.qualifyingDays} QDs)</span>
                    <span className="font-mono">{formatCurrency(results.newSSP.dailyRate)}</span>
                  </div>
                  <div className="flex justify-between text-accent-700">
                    <span>Waiting days</span>
                    <span className="font-mono">0 (abolished)</span>
                  </div>
                  <div className="flex justify-between text-accent-700">
                    <span>SSP days paid</span>
                    <span className="font-mono">{results.newSSP.sspDaysPaid}</span>
                  </div>
                  <div className="flex justify-between pt-2 border-t border-accent-200 font-semibold text-accent-900">
                    <span>Total SSP</span>
                    <span className="font-mono text-lg">{formatCurrency(results.newSSP.totalSSP)}</span>
                  </div>
                </div>
              </div>
              
              {/* Difference */}
              <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-red-900">Additional Cost (2026)</span>
                  <span className="text-xl font-bold text-red-900 font-mono">
                    +{formatCurrency(results.newSSP.totalSSP - (results.currentSSP.totalSSP || 0))}
                  </span>
                </div>
              </div>

              {/* 28-Week Maximum Warning */}
              {((results.currentSSP.weeksUsed || 0) > 0 || (results.newSSP.weeksUsed || 0) > 0) && (
                <div className={`p-4 rounded-lg border ${
                  (results.currentSSP.maxReached || results.newSSP.maxReached)
                    ? 'bg-red-50 border-red-300'
                    : ((results.currentSSP.weeksRemaining || 28) < 4 || (results.newSSP.weeksRemaining || 28) < 4)
                    ? 'bg-amber-50 border-amber-300'
                    : 'bg-blue-50 border-blue-300'
                }`}>
                  <div className="flex items-start gap-3">
                    <AlertCircle className={`w-5 h-5 mt-0.5 ${
                      (results.currentSSP.maxReached || results.newSSP.maxReached)
                        ? 'text-red-600'
                        : ((results.currentSSP.weeksRemaining || 28) < 4 || (results.newSSP.weeksRemaining || 28) < 4)
                        ? 'text-amber-600'
                        : 'text-blue-600'
                    }`} />
                    <div className="flex-1 text-sm">
                      <p className={`font-semibold mb-2 ${
                        (results.currentSSP.maxReached || results.newSSP.maxReached)
                          ? 'text-red-900'
                          : ((results.currentSSP.weeksRemaining || 28) < 4 || (results.newSSP.weeksRemaining || 28) < 4)
                          ? 'text-amber-900'
                          : 'text-blue-900'
                      }`}>
                        28-Week SSP Maximum Tracking
                      </p>
                      <div className="space-y-1.5">
                        {(results.currentSSP.weeksUsed || 0) > 0 && (
                          <div className={
                            results.currentSSP.maxReached
                              ? 'text-red-800'
                              : (results.currentSSP.weeksRemaining || 28) < 4
                              ? 'text-amber-800'
                              : 'text-blue-800'
                          }>
                            <span className="font-medium">Current rules:</span> {(results.currentSSP.weeksUsed || 0).toFixed(1)} weeks used
                            {results.currentSSP.wasCapped && <span className="ml-1 font-semibold">(payment capped)</span>}
                            <span className="mx-2">•</span>
                            {(results.currentSSP.weeksRemaining || 28).toFixed(1)} weeks remaining
                          </div>
                        )}
                        {(results.newSSP.weeksUsed || 0) > 0 && (
                          <div className={
                            results.newSSP.maxReached
                              ? 'text-red-800'
                              : (results.newSSP.weeksRemaining || 28) < 4
                              ? 'text-amber-800'
                              : 'text-blue-800'
                          }>
                            <span className="font-medium">2026 rules:</span> {(results.newSSP.weeksUsed || 0).toFixed(1)} weeks used
                            {results.newSSP.wasCapped && <span className="ml-1 font-semibold">(payment capped)</span>}
                            <span className="mx-2">•</span>
                            {(results.newSSP.weeksRemaining || 28).toFixed(1)} weeks remaining
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Backfill Cost */}
          <div className="card">
            <div className="card-header">
              <h2 className="font-semibold text-slate-900">Backfill Cost</h2>
            </div>
            <div className="card-body">
              <div className="space-y-2 text-sm">
                <div className="flex justify-between text-slate-600">
                  <span>Shifts to cover</span>
                  <span className="font-mono">{absence.rosteredDays}</span>
                </div>
                <div className="flex justify-between text-slate-600">
                  <span>Hours per shift</span>
                  <span className="font-mono">{employee.typicalShiftLength}h</span>
                </div>
                <div className="flex justify-between text-slate-600">
                  <span>Rate</span>
                  <span className="font-mono">{formatCurrency(employee.hourlyRate)}/hr</span>
                </div>
                <div className="flex justify-between pt-2 border-t border-slate-200 font-semibold">
                  <span>Backfill Cost</span>
                  <span className="font-mono">{formatCurrency(results.backfill.totalCost)}</span>
                </div>
              </div>
            </div>
          </div>
          
          {/* Total Cost */}
          <div className="card bg-slate-900 text-white">
            <div className="card-body">
              <h3 className="text-sm font-medium text-slate-300 mb-4">Total Absence Cost</h3>
              
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-slate-300">Current (SSP + Backfill)</span>
                  <span className="font-mono font-semibold">
                    {formatCurrency((results.currentSSP.totalSSP || 0) + results.backfill.totalCost)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-300">2026 (SSP + Backfill)</span>
                  <span className="font-mono font-semibold">
                    {formatCurrency(results.newSSP.totalSSP + results.backfill.totalCost)}
                  </span>
                </div>
                <div className="flex justify-between items-center pt-3 border-t border-slate-700">
                  <span className="font-medium">Increase</span>
                  <span className="text-xl font-bold font-mono text-accent-400">
                    +{formatCurrency(results.newSSP.totalSSP - (results.currentSSP.totalSSP || 0))}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
